import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getArrivalsAPI,
  getArrivalDetailsAPI,
  getArrivalsCalendarAPI,
  saveArrivalUpdatesAPI,
  generateReservationSummaryAPI,
  toggleArrivalChecklistAPI,
} from "../../api/arrivals";

//    FETCH ARRIVALS LIST

export const getArrivals = createAsyncThunk(
  "arrivals/getArrivals",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getArrivalsAPI(payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//    FETCH ARRIVAL DETAILS

export const getArrivalDetails = createAsyncThunk(
  "arrivals/getArrivalDetails",
  async ({ reservationId, payload }, { rejectWithValue }) => {
    try {
      const response = await getArrivalDetailsAPI(reservationId, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//    FETCH ARRIVALS CALENDAR

export const getArrivalsCalendar = createAsyncThunk(
  "arrivalsCalendar/get",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getArrivalsCalendarAPI(payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//    SAVE ARRIVAL UPDATES

export const saveArrivalUpdates = createAsyncThunk(
  "arrivals/saveArrivalUpdates",
  async ({ reservationId, payload }, { rejectWithValue }) => {
    try {
      const response = await saveArrivalUpdatesAPI(reservationId, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//    GENERATE RESERVATION SUMMARY

export const generateReservationSummary = createAsyncThunk(
  "arrivals/generateReservationSummary",
  async ({ reservationId, idempotencyKey }, { rejectWithValue }) => {
    try {
      const response = await generateReservationSummaryAPI(reservationId, idempotencyKey);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const toggleArrivalChecklist = createAsyncThunk(
  "arrivals/toggleChecklist",
  async ({ reservationId, itemId, isChecked }, { rejectWithValue }) => {
    try {
      const response = await toggleArrivalChecklistAPI(reservationId, { itemId, isChecked });
      if (!response.success) return rejectWithValue(response);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.response?.data?.message || err.message,
        status: err.response?.status,
      });
    }
  },
  {
    condition: ({ reservationId }, { getState }) =>
      !getState().arrivals.checklistSaving[reservationId],
  }
);

const arrivalsSlice = createSlice({
  name: "arrivals",

  initialState: {
    list: [],
    arrivalDetails: null,
    checklistSaving: {},

    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
    },

    filters: {
      search: "",
      fromUtc: null,
      toUtc: null,
      hotelIds: [],
      sortBy: "datetime",
      sortDir: "asc",
    },

    calendar: {
      data: {},
      loading: false,
      error: null,
      monthKey: null,
    },

    fetchLoading: false,
    fetchError: null,

    detailsLoading: false,
    detailsError: null,

    saveLoading: false,
    saveError: null,
    saveSuccess: false,

    summaryGenerating: false,
    summaryError: null,
  },

  reducers: {
    clearArrivalDetails: (state) => {
      state.arrivalDetails = null;
    },

    resetArrivalsState: (state) => {
      state.fetchLoading = false;
      state.fetchError = null;
      state.list = [];
    },

    setArrivalsFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    resetSaveArrivalState: (state) => {
      state.saveLoading = false;
      state.saveError = null;
      state.saveSuccess = false;
      state.summaryError = null;
    },

    clearArrivalsCalendar: (state) => {
      state.calendar.data = {};
      state.calendar.loading = false;
      state.calendar.error = null;
      state.calendar.monthKey = null;
    },
  },

  extraReducers: (builder) => {
    builder
      /* ───────── TOGGLE ARRIVAL CHECKLIST ───────── */

      .addCase(toggleArrivalChecklist.pending, (state, action) => {
        state.checklistSaving[action.meta.arg.reservationId] = true;
      })

      .addCase(toggleArrivalChecklist.fulfilled, (state, action) => {
        delete state.checklistSaving[action.meta.arg.reservationId];

        const { reservationId, itemId, isChecked, prepProgressPct } = action.payload.data;

        const arrival = state.list.find((item) => item._id === reservationId);

        if (arrival) {
          const item = arrival.prepChecklistItems?.find((item) => item._id === itemId);
          if (item) item.isChecked = isChecked;
          arrival.prepProgressPct = prepProgressPct;
        }
      })

      .addCase(toggleArrivalChecklist.rejected, (state, action) => {
        delete state.checklistSaving[action.meta.arg.reservationId];
      })

      /* ───────── FETCH ARRIVALS CALENDAR ───────── */

      .addCase(getArrivalsCalendar.pending, (state) => {
        state.calendar.loading = true;
        state.calendar.error = null;
      })

      .addCase(getArrivalsCalendar.fulfilled, (state, action) => {
        state.calendar.loading = false;

        const { totals = {}, days = [] } = action.payload?.data || {};

        // convert array -> object for fast lookup
        const daysMap = {};

        days.forEach((d) => {
          daysMap[d.day] = {
            arrivals: d.arrivals,
            departures: d.departures,
          };
        });

        state.calendar.data = {
          totalArrivals: totals.arrivals || 0,
          totalDepartures: totals.departures || 0,
          days: daysMap,
        };
      })

      .addCase(getArrivalsCalendar.rejected, (state, action) => {
        state.calendar.loading = false;
        state.calendar.error = action.payload;
      })

      /* ───────── FETCH ARRIVALS ───────── */

      .addCase(getArrivals.pending, (state) => {
        state.fetchLoading = true;
        state.fetchError = null;
      })

      .addCase(getArrivals.fulfilled, (state, action) => {
        state.fetchLoading = false;

        const { items = [], page = 1, pageSize = 10, total = 0 } = action.payload?.data || {};

        state.list = items;

        state.pagination = {
          page,
          pageSize,
          total,
        };
      })

      .addCase(getArrivals.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload;
      })

      /* ───────── FETCH ARRIVAL DETAILS ───────── */

      .addCase(getArrivalDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })

      .addCase(getArrivalDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.arrivalDetails = action.payload?.data || null;
      })

      .addCase(getArrivalDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      })

      /* ───────── SAVE ARRIVAL UPDATES ───────── */

      .addCase(saveArrivalUpdates.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
        state.saveSuccess = false;
      })

      .addCase(saveArrivalUpdates.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;

        // refresh details with updated backend response
        state.arrivalDetails = action.payload?.data || null;
      })

      .addCase(saveArrivalUpdates.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError = action.payload;
      })

      // ─────────  GENERATE AI SUMMARY ─────────
      .addCase(generateReservationSummary.pending, (state) => {
        state.summaryGenerating = true;
        state.summaryError = null;
      })

      .addCase(generateReservationSummary.fulfilled, (state, action) => {
        state.summaryGenerating = false;

        if (state.arrivalDetails) {
          state.arrivalDetails.aiSummary = action.payload?.data?.summary || null;
        }
      })

      .addCase(generateReservationSummary.rejected, (state, action) => {
        state.summaryGenerating = false;
        state.summaryError = action.payload;
      });
  },
});

export const {
  clearArrivalDetails,
  resetArrivalsState,
  setArrivalsFilters,
  clearArrivalsCalendar,
  resetSaveArrivalState,
} = arrivalsSlice.actions;

export default arrivalsSlice.reducer;
